import {
	Card,
	CardHeader,
	CardBody,
	CardDivider,
	withFilters,
	Button,
	FormFileUpload,
	__experimentalHStack as HStack,
	__experimentalVStack as VStack,
} from '@wordpress/components';
import { Fragment, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { dateI18n } from '@wordpress/date';
import { downloadBlob } from '@wordpress/blob';
import { useDispatch } from '@wordpress/data';
import { store as noticesStore } from '@wordpress/notices';
import { addQueryArgs } from '@wordpress/url';
import apiFetch from '@wordpress/api-fetch';

const ExportPlaceholder = withFilters( 'popper.settings.tools.export' )( () => {
	return <Fragment />;
} );

const ImportPlaceholder = withFilters( 'popper.settings.tools.import' )( () => {
	return <Fragment />;
} );

function Importer() {
	const [ loading, setLoading ] = useState( false );
	const [ files, setFiles ] = useState( [] );
	const { createNotice } = useDispatch( noticesStore );

	const importPopups = () => {
		const fileReader = new FileReader();
		fileReader.onloadend = ( e ) => {
			const content = e.target.result;
			const result = JSON.parse( content );
			importReqs( result );
		};
		fileReader.readAsText( files[ 0 ] );
	};

	const importReqs = async ( objs ) => {
		const reqs = { requests: [] };
		for ( const p of objs ) {
			const req = {
				method: 'POST',
				path: '/wp/v2/popper',
				body: {
					title: p.title.rendered,
					excerpt: p.excerpt.rendered,
					content: p.content.rendered,
					status: 'draft',
				},
			};
			reqs.requests.push( req );
		}
		setLoading( true );
		try {
			const res = await apiFetch( {
				path: '/batch/v1/',
				method: 'POST',
				data: reqs,
			} );
			const errors = [];
			res.responses.forEach( ( a ) => {
				if ( a.status !== 201 ) {
					errors.push( a.body.message );
				}
			} );
			if ( ! errors.length ) {
				setFiles( [] );
				createNotice( 'success', '🎯 ' + __( 'Popups imported' ), {
					type: 'snackbar',
					explicitDismiss: true,
				} );
			} else {
				createNotice(
					'error',
					'⚠️ ' + __( 'Ops. An error happened!', 'popper' ),
					{
						type: 'snackbar',
					}
				);
			}
			setLoading( false );
		} catch ( err ) {
			setFiles( [] );
			createNotice( 'error', '⚠️ ' + err.message, {
				type: 'snackbar',
				explicitDismiss: true,
			} );
		}
	};

	return (
		<Fragment>
			<h2>{ __( 'Import' ) }</h2>
			<p>
				{ __(
					'You can use this tool to import all popup previously exported using this tool.',
					'popper'
				) }
			</p>
			<HStack justify="flex-start">
				<FormFileUpload
					accept="application/json"
					onChange={ ( event ) => setFiles( event.target.files ) }
					variant="secondary"
					__next40pxDefaultSize
				>
					{ files.length
						? files[ 0 ]?.name
						: __( 'Choose file', 'formello' ) }
				</FormFileUpload>
				{ files.length && (
					<Button
						variant="primary"
						text={ __( 'Upload' ) }
						onClick={ importPopups }
						isBusy={ loading }
						disabled={ loading }
						__next40pxDefaultSize
					/>
				) }
			</HStack>
			<ImportPlaceholder />
		</Fragment>
	);
}

const Tools = () => {
	const currentDate = dateI18n( '', new Date() );
	const [ loading, setLoading ] = useState( false );

	const exportPopups = () => {
		setLoading( true );
		apiFetch( {
			path: addQueryArgs( '/wp/v2/popper', {
				_embed: 'wp:term,author',
				per_page: -1,
			} ),
		} ).then( ( popups ) => {
			const fileContent = JSON.stringify( popups, null, 2 );
			const filename = `popper-export-${ currentDate }.json`;

			downloadBlob( filename, fileContent, 'application/json' );
			setLoading( false );
		} );
	};

	return (
		<Fragment>
			<Card>
				<CardHeader size="small">
					<b>{ __( 'Import/Export', 'popper' ) }</b>
				</CardHeader>

				<CardBody>
					<VStack>
						<h2>{ __( 'Export' ) }</h2>
						<p>
							{ __(
								'You can use this tool to export all your popups.',
								'popper'
							) }
						</p>
						<HStack justify={ 'flex-start' }>
							<Button
								onClick={ () => exportPopups() }
								text={ __( 'Export popups', 'popper' ) }
								variant="primary"
								disabled={ loading }
								isBusy={ loading }
							/>
						</HStack>
						<ExportPlaceholder
							exportPopups={ exportPopups }
							disabled={ loading }
						/>
						<CardDivider />
						<Importer />
					</VStack>
				</CardBody>
			</Card>
		</Fragment>
	);
};

export default withFilters( 'popper.settings.tools' )( Tools );
